import React, { useState } from 'react';
import {
    Box,
    TextField,
    Button,
    Typography,
    Paper,
    Alert,
    CircularProgress,
    InputAdornment,
} from '@mui/material';
import { useNavigate, Link } from 'react-router-dom';
import apiClient from '../../services/apiClient';

/**
 * Self-service registration page for new organizations.
 * Creates an Organization + first admin User in a single transaction.
 */
const RegisterPage = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        orgName: '',
        orgSlug: '',
        adminEmail: '',
        adminPassword: '',
        adminFirstName: '',
        adminLastName: '',
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

    const generateSlug = (name) => {
        return name
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '')
            .substring(0, 50);
    };

    const handleChange = (field) => (e) => {
        const value = e.target.value;
        setFormData((prev) => {
            const updated = { ...prev, [field]: value };
            if (field === 'orgName' && !slugManuallyEdited) {
                updated.orgSlug = generateSlug(value);
            }
            return updated;
        });
        if (field === 'orgSlug') {
            setSlugManuallyEdited(true);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await apiClient.post('/api/onboarding/register', formData);
            const data = response.data;

            localStorage.setItem('authToken', data.token);
            localStorage.setItem('orgId', data.orgId);
            localStorage.setItem('orgSlug', data.orgSlug);

            navigate('/onboarding');
        } catch (err) {
            const message = err.response?.data?.message || err.response?.data?.error || 'Registration failed. Please try again.';
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#0a1929',
                p: 2,
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    p: 4,
                    maxWidth: 500,
                    width: '100%',
                    backgroundColor: '#0d2137',
                    borderRadius: 2,
                }}
            >
                <Typography variant="h4" sx={{ color: '#fff', mb: 1, textAlign: 'center' }}>
                    Create Your Account
                </Typography>
                <Typography variant="body2" sx={{ color: '#8899aa', mb: 3, textAlign: 'center' }}>
                    Start monitoring with VerOps — free plan included
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <form onSubmit={handleSubmit}>
                    <TextField
                        fullWidth
                        label="Organization Name"
                        value={formData.orgName}
                        onChange={handleChange('orgName')}
                        required
                        sx={{ mb: 2 }}
                        placeholder="Acme Corporation"
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        fullWidth
                        label="Organization Slug"
                        value={formData.orgSlug}
                        onChange={handleChange('orgSlug')}
                        required
                        sx={{ mb: 2 }}
                        placeholder="acme-corp"
                        helperText="URL-friendly identifier (auto-generated)"
                        InputProps={{
                            startAdornment: <InputAdornment position="start" sx={{ color: '#667' }}>org/</InputAdornment>,
                        }}
                        InputLabelProps={{ shrink: true }}
                    />

                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField
                            fullWidth
                            label="First Name"
                            value={formData.adminFirstName}
                            onChange={handleChange('adminFirstName')}
                            required
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            fullWidth
                            label="Last Name"
                            value={formData.adminLastName}
                            onChange={handleChange('adminLastName')}
                            required
                            InputLabelProps={{ shrink: true }}
                        />
                    </Box>

                    <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={formData.adminEmail}
                        onChange={handleChange('adminEmail')}
                        required
                        sx={{ mb: 2 }}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        fullWidth
                        label="Password"
                        type="password"
                        value={formData.adminPassword}
                        onChange={handleChange('adminPassword')}
                        required
                        sx={{ mb: 3 }}
                        helperText="Minimum 8 characters"
                        InputLabelProps={{ shrink: true }}
                    />

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={loading}
                        sx={{
                            py: 1.5,
                            backgroundColor: '#1976d2',
                            '&:hover': { backgroundColor: '#1565c0' },
                            mb: 2,
                        }}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Create Account'}
                    </Button>

                    <Typography variant="body2" sx={{ textAlign: 'center', color: '#8899aa' }}>
                        Already have an account?{' '}
                        <Link to="/" style={{ color: '#42a5f5' }}>
                            Sign in
                        </Link>
                    </Typography>
                </form>
            </Paper>
        </Box>
    );
};

export default RegisterPage;
